import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreHorizontal, ChevronDown, ChevronUp, Search, ChevronsUpDown, Eye, Pencil, Archive } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, fmtUS as fmtDate, today } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Select } from "@/components/common";
import { InlineDate } from "@/components/InlineDate";
import { PrepBadge } from "@/components/PrepBadge";

export function NavigatorPicker({ value, label, onPick, testid, placeholder = "Assign navigator" }) {
  const { meta } = useApp();
  const [open, setOpen] = useState(false);
  const staff = (meta?.staff || []).filter((s) => s.active !== false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex h-7 w-full min-w-[150px] items-center justify-between gap-1 rounded border border-transparent px-1.5 text-left text-[12px] hover:border-[hsl(var(--border))]" data-testid={testid} onClick={(e) => e.stopPropagation()}>
          <span className={label ? "" : "text-[var(--muted-ink)]"}>{label || placeholder}</span><ChevronsUpDown size={12} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search employee…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No employee found</CommandEmpty>
            <CommandGroup>
              {staff.map((s) => (
                <CommandItem key={s.id} value={s.name} onSelect={() => { setOpen(false); if (s.id !== value) onPick(s); }} data-testid={`${testid}-option-${s.id}`}>
                  {s.name}<span className="ml-auto text-[10px] capitalize text-[var(--muted-ink)]">{s.role === "employee" ? "Navigator" : s.role}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const PCT = (r) => (r.fill_pct == null && r.insufficient_data ? <span className="text-[10px] font-semibold text-amber-700" title="PrEP Start Date is missing" data-testid={`insufficient-${r.patient_id}`}>Insufficient Data</span> : r.fill_pct == null ? <span className="text-[var(--muted-ink)]" title="Expected fills cannot be determined from available data">N/A</span>
  : <span className={r.fill_flag ? "font-semibold text-amber-600" : r.fill_pct < 80 ? "font-semibold text-red-600" : "font-semibold text-emerald-700"} title={r.fill_flag || `${r.adherence_recorded} of ${r.expected_fills} expected months covered since PrEP start`}>{r.fill_pct}%{r.fill_flag ? " !" : ""}</span>);

function RowActions({ r, onRemove, onOpen }) {
  const { can } = useApp();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button className="rounded p-1 hover:bg-[var(--surface)]" onClick={(e) => e.stopPropagation()} data-testid={`row-actions-${r.patient_id}`}><MoreHorizontal size={15} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onOpen(`/patients/${r.id}`)} data-testid={`action-view-${r.patient_id}`}><Eye size={13} className="mr-2" />View Patient</DropdownMenuItem>
        {can("action:patient_edit") && <DropdownMenuItem onSelect={() => onOpen(`/patients/${r.id}/edit`)} data-testid={`action-edit-${r.patient_id}`}><Pencil size={13} className="mr-2" />Edit Patient</DropdownMenuItem>}
        {can("action:patient_remove") && <DropdownMenuItem className="text-red-600" onSelect={() => onRemove(r)} data-testid={`action-remove-${r.patient_id}`}><Archive size={13} className="mr-2" />Delete Patient</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const PAGE = 50;

export function PatientTable({ initialFilters = {}, refreshKey, onChanged, view }) {
  const { meta, user, can } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const storeKey = `ptbl:${loc.pathname}${loc.search}`;
  const init = useRef(undefined);
  if (init.current === undefined) {
    try { init.current = JSON.parse(sessionStorage.getItem(storeKey) || "null"); } catch (e) { init.current = null; }
  }
  const sv = init.current || {};
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState(sv.f || { ...initialFilters });
  const [q, setQ] = useState(sv.q || "");
  const [dq, setDq] = useState((sv.q || "").trim());
  const [cycle, setCycle] = useState(sv.cycle || "0");
  const [sort, setSort] = useState(sv.sort || { key: "patient_id", dir: 1 });
  const [sel, setSel] = useState([]);
  const scroller = useRef(null);
  const sentinel = useRef(null);
  const reqId = useRef(0);
  const restore = useRef(null);
  const rowsRef = useRef([]);
  const lastFetch = useRef(null);
  const [bulkEmp, setBulkEmp] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [n, setN] = useState(0);
  const canAssign = user.role === "admin" || can("action:patient_assign");
  const canRemove = user.role === "admin" || can("action:patient_remove");
  const canSelect = canAssign || canRemove;
  const [bulkDel, setBulkDel] = useState(false);
  const [selAllBusy, setSelAllBusy] = useState(false);
  const filterParams = () => ({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), cycle_offset: cycle, patient_id: dq || undefined, view });
  useEffect(() => { setSel([]); }, [f, dq]);
  const allSel = total > 0 && sel.length >= total;
  const toggleAll = async () => {
    if (allSel) return setSel([]);
    setSelAllBusy(true);
    try {
      const { data } = await api.get("/patients", { params: { ...filterParams(), ids_only: 1 } });
      setSel(data.ids);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSelAllBusy(false);
    }
  };
  const bulkRemove = async () => {
    try {
      const { data } = await api.post("/patients/bulk-archive", { ids: sel });
      if (data.archived.length) toast.success(`${data.archived.length} patient(s) archived. History preserved.`);
      if (data.failed.length) toast.error(`${data.failed.length} not archived: ${data.failed.map((x) => `${x.patient_id || x.id} (${x.error})`).join("; ")}`, { duration: 10000 });
      setBulkDel(false);
      setSel([]);
      setN((x) => x + 1);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const showNavFilter = user.role !== "employee" && (meta?.staff || []).length > 1;
  const editLabs = user.role === "admin" || can("action:patient_edit");
  const editRx = user.role === "admin" || can("action:record_edit");
  const refresh = () => setN((x) => x + 1);
  const dateCell = (r, field, value, label, content, tid) => (field === "rx_date" ? editRx : editLabs) && (field !== "rx_date" || r.medication)
    ? <InlineDate patient={r} field={field} value={value} label={label} onSaved={refresh} testid={tid}>{content}</InlineDate> : content;
  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const fetchPage = useCallback((skip, limit) => {
    const id = ++reqId.current;
    setLoading(true);
    const params = { ...filterParams(), sort: sort.key, dir: sort.dir, skip, limit };
    return api.get("/patients", { params }).then(({ data }) => {
      if (id !== reqId.current) return;
      const base = skip ? rowsRef.current : [];
      const ids = new Set(base.map((r) => r.id));
      const next = [...base, ...data.items.filter((r) => !ids.has(r.id))];
      rowsRef.current = next;
      setRows(next);
      setTotal(data.total);
      setYear(data.cycle_year);
    }).catch(() => { if (id === reqId.current) { rowsRef.current = []; setRows([]); setTotal(0); } })
      .finally(() => { if (id === reqId.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, cycle, dq, sort]);
  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(storeKey) || "null"); } catch (e) { saved = null; }
    restore.current = saved;
    if (!saved && lastFetch.current !== fetchPage && scroller.current) scroller.current.scrollTop = 0;
    lastFetch.current = fetchPage;
    const keep = Math.max(PAGE, saved?.count || 0, rowsRef.current.length && n ? rowsRef.current.length : 0);
    fetchPage(0, Math.min(500, keep));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage, refreshKey, n]);
  useEffect(() => {
    if (rows && restore.current && scroller.current) {
      scroller.current.scrollTop = restore.current.top;
      restore.current = null;
      sessionStorage.removeItem(storeKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
  const more = rows !== null && rows.length < total;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !more) return undefined;
    const ob = new IntersectionObserver((es) => {
      if (es[0].isIntersecting && !loading) fetchPage(rowsRef.current.length, PAGE);
    }, { root: scroller.current, rootMargin: "300px" });
    ob.observe(el);
    return () => ob.disconnect();
  }, [more, loading, fetchPage]);
  const openPatient = (r, path) => {
    sessionStorage.setItem(storeKey, JSON.stringify({ top: scroller.current?.scrollTop || 0, count: rowsRef.current.length, f, q, sort, cycle }));
    nav(path || `/patients/${r.id}`);
  };

  const assign = async (ids, emp) => {
    try {
      const { data } = await api.post("/assignments/bulk", { patient_ids: ids, employee_id: emp.id, reason: ids.length > 1 ? "Bulk assignment" : "Transfer" });
      toast.success(`${data.transferred} patient(s) assigned to ${emp.name}. History preserved.`);
      setSel([]);
      setBulkEmp(null);
      setN((x) => x + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    try {
      await api.post(`/patients/${removing.id}/archive`, {});
      toast.success(`Patient ${removing.patient_id} archived. History preserved.`);
      setRemoving(null);
      setN((x) => x + 1);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cols = [
    { key: "dob", label: "DOB", cls: "whitespace-nowrap", render: (r) => <span className="font-medium text-[#20336F] dark:text-blue-100" data-testid={`dob-${r.patient_id}`}>{fmtDate(r.dob)}</span> },
    { key: "medication", label: "Medication" },
    { key: "insurance", label: "Insurance", render: (r) => <span className="block max-w-[140px] truncate" title={r.insurance}>{r.insurance || "—"}</span> },
    { key: "assigned_employee", label: "Wellness Navigator", render: (r) => (canAssign
      ? <NavigatorPicker value={r.assigned_employee_id} label={r.assigned_employee} testid={`navigator-${r.patient_id}`} onPick={(emp) => assign([r.id], emp)} />
      : r.assigned_employee || "—") },
    { key: "last_lab", label: "Last Labs", render: (r) => dateCell(r, "last_lab", r.last_lab, "Last Labs", fmtDate(r.last_lab), `edit-last-lab-${r.patient_id}`) },
    { key: "next_lab", label: "Next Labs", cls: "nextlab", render: (r) => dateCell(r, "next_lab", r.next_lab, "Next Labs", <span className={r.next_lab && r.next_lab < today() ? "font-bold text-red-600" : "font-bold text-[#20336F] dark:text-blue-200"} data-testid={`next-lab-${r.patient_id}`}>{fmtDate(r.next_lab)}</span>, `edit-next-lab-${r.patient_id}`) },
  ];
  const rxCols = [0, 1, 2, 3].flatMap((i) => [
    { key: `rx${i}d`, label: "Rx Date", val: (r) => r.rx_periods[i]?.date, render: (r) => dateCell(r, "rx_date", r.rx_periods[i]?.date, `Rx Date ${i + 1}`, <span title={(r.rx_periods[i]?.dates || []).map(fmtDate).join(", ")}>{fmtDate(r.rx_periods[i]?.date)}{(r.rx_periods[i]?.dates || []).length > 1 ? ` +${r.rx_periods[i].dates.length - 1}` : ""}</span>, `edit-rx${i}-${r.patient_id}`), cls: "border-l" },
    { key: `rx${i}f`, label: "Fills", val: (r) => r.rx_periods[i]?.fills, render: (r) => <b>{r.rx_periods[i]?.fills ?? "—"}</b>, cls: "text-center" },
  ]);
  const tail = [
    { key: "total_fills", label: "Rx Filled", render: (r) => <b data-testid={`total-fills-${r.patient_id}`}>{r.total_fills ?? "—"}</b>, cls: "border-l text-center" },
    { key: "fill_pct", label: "Filled %", render: (r) => <span data-testid={`fill-pct-${r.patient_id}`}>{PCT(r)}</span>, cls: "text-center" },
  ];
  const shown = rows || [];
  const SortTh = ({ c, className = "", ...rest }) => (
    <th {...rest} className={`cursor-pointer select-none ${className}`} onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}>
      <span className="inline-flex items-center gap-0.5">{c.label}{sort.key === c.key && (sort.dir === 1 ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}</span>
    </th>
  );
  if (rows === null) return <div className="panel p-8 text-center text-sm text-[var(--muted-ink)]">Loading patients…</div>;
  return (
    <div className="panel overflow-hidden" data-testid="patient-table">
      <div className="no-print flex flex-col gap-2 border-b p-2.5 md:flex-row md:flex-wrap md:items-center">
        <div className="relative md:w-56">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted-ink)]" />
          <input className="inp h-8 pl-7 text-[13px]" placeholder="Search Patient ID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="patient-table-search" />
        </div>
        <Select className="h-8 text-[13px] md:w-40" value={f.medication} onChange={(v) => setF({ ...f, medication: v })} placeholder="All medications" testid="filter-medication" options={(meta?.medications || []).map((m) => m.name)} />
        {showNavFilter && <Select className="h-8 text-[13px] md:w-48" value={f.assigned_employee_id} onChange={(v) => setF({ ...f, assigned_employee_id: v })} placeholder="All Wellness Navigators" testid="filter-employee" options={[...meta.staff.filter((s) => s.active !== false || s.patient_count).sort((a, b) => (b.patient_count || 0) - (a.patient_count || 0) || a.name.localeCompare(b.name)).map((s) => ({ value: s.id, label: `${s.name}${s.role === "supervisor" ? " (Supervisor)" : ""} · ${s.patient_count || 0}` })), ...(view === "mine" ? [] : [{ value: "unassigned", label: "Unassigned Patients" }])]} />}
        <Select className="h-8 text-[13px] md:w-40" value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="All Patients" testid="filter-status" options={[{ value: "ACTIVE", label: "Active" }, { value: "DISCHARGED", label: "Discharged" }, { value: "RE-ENGAGE", label: "Re-Engage" }]} />
        <Select className="h-8 text-[13px] md:w-60" value={cycle} onChange={setCycle} testid="filter-cycle" options={[0, 1, 2].map((o) => ({ value: String(o), label: `${o === 0 ? "Current cycle" : "Cycle"} ${new Date().getFullYear() - o} (Jan–Dec)` }))} />
        <div className="ml-auto text-xs text-[var(--muted-ink)]" data-testid="patient-table-count">{total} {total === 1 ? "patient" : "patients"}</div>
      </div>
      {canSelect && sel.length > 0 && (
        <div className="no-print flex flex-wrap items-center gap-2 border-b bg-blue-50 px-3 py-1.5 dark:bg-white/5" data-testid="bulk-action-bar">
          <span className="text-xs font-semibold text-[#283F91] dark:text-blue-200" data-testid="bulk-selected-count">{sel.length} {sel.length === 1 ? "Patient" : "Patients"} Selected</span>
          <span className="text-[var(--muted-ink)]">|</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setSel([])} data-testid="bulk-clear-btn">Clear Selection</button>
          {canAssign && <><span className="text-[var(--muted-ink)]">|</span>
            <div className="w-48 rounded border bg-[var(--panel)]"><NavigatorPicker value={null} label={bulkEmp?.name} testid="bulk-navigator" placeholder="Choose navigator…" onPick={setBulkEmp} /></div>
            <button className="btn btn-primary btn-sm" disabled={!bulkEmp} onClick={() => assign(sel, bulkEmp)} data-testid="bulk-assign-btn">Assign selected</button></>}
          {canRemove && <><span className="text-[var(--muted-ink)]">|</span>
            <button className="btn btn-sm bg-red-600 text-white hover:bg-red-700" onClick={() => setBulkDel(true)} data-testid="bulk-delete-btn"><Archive size={13} className="mr-1" />Delete Selected</button></>}
        </div>
      )}
      <div className="max-h-[70vh] overflow-auto" ref={scroller} data-testid="patient-table-scroll">
        <table className="ptbl w-full">
          <thead>
            <tr>
              <SortTh c={{ key: "patient_id", label: <>{canSelect && <input type="checkbox" className="mr-1 h-3.5 w-3.5" checked={allSel} disabled={selAllBusy || !total} ref={(el) => { if (el) el.indeterminate = sel.length > 0 && !allSel; }} onClick={(e) => e.stopPropagation()} onChange={toggleAll} title={`Select all ${total} filtered patients`} data-testid="select-all-patients" />}Patient ID</> }} rowSpan={2} className="sticky left-0 z-[4]" />
              {cols.map((c) => <SortTh key={c.key} c={c} rowSpan={2} className={c.cls || ""} />)}
              <th colSpan={8} className="border-l text-center text-[#283F91] dark:text-blue-300" data-testid="rx-history-header">Prescription History — {year || "12 Months"}</th>
              {tail.map((c) => <SortTh key={c.key} c={c} rowSpan={2} className="border-l" />)}
              <th rowSpan={2} className="sticky right-0 z-[4] text-center">Actions</th>
            </tr>
            <tr>{rxCols.map((c) => <SortTh key={c.key} c={c} className={`second ${c.cls || ""}`} />)}</tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} data-testid="patient-table-row" className="cursor-pointer" onClick={() => openPatient(r)}>
                <td className="sticky left-0 z-[1] font-semibold text-[#283F91] dark:text-blue-300">
                  <span className="inline-flex items-center gap-1.5">
                    {canSelect && <input type="checkbox" className="h-3.5 w-3.5" checked={sel.includes(r.id)} onClick={(e) => e.stopPropagation()}
                      onChange={() => setSel((s) => (s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id]))} data-testid={`select-patient-${r.patient_id}`} />}
                    {r.patient_id}{r.is_draft && <span className="text-[9px] text-amber-600">DRAFT</span>}
                    <PrepBadge start={r.prep_start} year={r.cycle_year} testid={`prep-badge-${r.patient_id}`} />
                  </span>
                </td>
                {cols.map((c) => <td key={c.key} className={c.cls || ""} onClick={["assigned_employee", "last_lab", "next_lab"].includes(c.key) ? (e) => e.stopPropagation() : undefined}>{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
                {rxCols.map((c) => <td key={c.key} className={c.cls} onClick={c.key.endsWith("d") ? (e) => e.stopPropagation() : undefined}>{c.render(r)}</td>)}
                {tail.map((c) => <td key={c.key} className={c.cls}>{c.render(r)}</td>)}
                <td className="sticky right-0 z-[1] text-center" onClick={(e) => e.stopPropagation()}><RowActions r={r} onRemove={setRemoving} onOpen={(path) => openPatient(r, path)} /></td>
              </tr>
            ))}
            {!shown.length && !loading && <tr><td colSpan={20} className="py-8 text-center text-[var(--muted-ink)]">No patients found</td></tr>}
          </tbody>
        </table>
        <div ref={sentinel} className="h-px" />
        {loading && <div className="no-print py-2 text-center text-xs text-[var(--muted-ink)]" data-testid="patient-table-loading">Loading patients…</div>}
        {!more && shown.length > 0 && !loading && <div className="no-print py-2 text-center text-[11px] text-[var(--muted-ink)]" data-testid="patient-table-end">All {total} {total === 1 ? "patient" : "patients"} loaded</div>}
      </div>
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent data-testid="remove-patient-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete patient {removing?.patient_id}?</AlertDialogTitle>
            <AlertDialogDescription>The patient will be archived and hidden from lists. Prescriptions, assignments, history and audit logs are preserved, and an administrator can restore the record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="remove-confirm-btn">Delete patient</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="bulk-delete-title">Delete {sel.length} selected {sel.length === 1 ? "patient" : "patients"}?</AlertDialogTitle>
            <AlertDialogDescription>{sel.length} {sel.length === 1 ? "patient" : "patients"} will be archived and removed from the active patient list. Prescriptions, dispensing records, labs, appointments, notes, assignment history and audit logs are preserved, and an administrator can restore them from Archived patients.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={bulkRemove} data-testid="bulk-delete-confirm">Archive {sel.length} {sel.length === 1 ? "patient" : "patients"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
